// File: frontend/src/pages/Dashboard/DashboardPage.jsx
// Main dashboard page for WA Contact Center CRM

import React, { useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useQuery, useQueryClient } from 'react-query';
import api, { getAnalyticsDashboard } from '../../services/api';
import DashboardStats from '../../components/Dashboard/DashboardStats';
import LeadScoreChart from '../../components/Dashboard/LeadScoreChart';
import PipelineOverview from '../../components/Dashboard/PipelineOverview';
import RecentActivities from '../../components/Dashboard/RecentActivities';
import CommercialSummary from '../../components/Dashboard/CommercialSummary';
import QuickActions from '../../components/Dashboard/QuickActions';

const DashboardPage = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  // Clear analytics cache on mount to ensure fresh data
  React.useEffect(() => {
    // Clear stale analytics cache when dashboard loads
    queryClient.invalidateQueries(['dashboard-analytics']);
  }, [queryClient]);

  // Fetch leads data for dashboard
  const { data: leadsData, isLoading: leadsLoading, error: leadsError } = useQuery(
    'dashboard-leads',
    async () => {
      const response = await api.get('/leads/stats/dashboard');
      return response.data.data || {};
    },
    {
      enabled: !!user?.id,
      refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes
      retry: 1,
    }
  );

  // Fetch contacts data for dashboard
  const { data: contactsData, isLoading: contactsLoading, error: contactsError } = useQuery(
    'dashboard-contacts',
    async () => {
      const response = await api.get('/contacts/stats/dashboard');
      return response.data.data || {};
    },
    {
      enabled: !!user?.id,
      refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes
      retry: 1,
    }
  );

  // Fetch companies data for dashboard
  const { data: companiesData, isLoading: companiesLoading, error: companiesError } = useQuery(
    'dashboard-companies',
    async () => {
      const response = await api.get('/companies/stats/dashboard');
      return response.data.data || {};
    },
    {
      enabled: !!user?.id,
      refetchInterval: 5 * 60 * 1000,
      retry: 1,
    }
  );

  // Fetch recent activities for dashboard
  const { data: activitiesData, isLoading: activitiesLoading, error: activitiesError } = useQuery(
    'dashboard-activities',
    async () => {
      try {
        const response = await api.get('/activities?limit=10&sort_by=created_at&sort_order=desc');
        return response.data.data || [];
      } catch (error) {
        console.error('❌ ACTIVITIES API ERROR:', error);
        throw error;
      }
    },
    {
      enabled: !!user?.id,
      refetchInterval: 5 * 60 * 1000,
      retry: 1,
    }
  );

  // Fetch analytics data for comprehensive dashboard
  const { data: analyticsData, isLoading: analyticsLoading, error: analyticsError } = useQuery(
    'dashboard-analytics',
    async () => {
      try {
        const response = await getAnalyticsDashboard(30);
        return response.data || {};
      } catch (error) {
        console.error('❌ Analytics API error using dedicated function:', error);
        console.error('❌ Analytics error response:', error.response?.data);
        console.error('❌ Analytics error status:', error.response?.status);
        throw error;
      }
    },
    {
      enabled: !!user?.id,
      refetchInterval: 5 * 60 * 1000,
      staleTime: 30 * 1000, // Consider stale after 30 seconds
      cacheTime: 60 * 1000, // Cache for 1 minute only
      refetchOnWindowFocus: true,
      refetchOnMount: 'always',
      retry: 1,
    }
  );

  const isLoading = leadsLoading || contactsLoading || companiesLoading || analyticsLoading || activitiesLoading;

  // Surface query failures to the UI instead of swallowing them. Each entry is
  // shown as a dismissible-looking banner so a blank screen never hides a failure.
  const queryErrors = [
    { label: 'leads', error: leadsError },
    { label: 'contatos', error: contactsError },
    { label: 'empresas', error: companiesError },
    { label: 'analytics', error: analyticsError },
    { label: 'atividades', error: activitiesError },
  ].filter((q) => q.error);

  // Show loading message if we have no data at all
  const showLoadingState = isLoading && !leadsData && !contactsData && !companiesData;

  // Calculate dashboard data from real API responses
  const dashboardData = useMemo(() => {
    if (!leadsData && !contactsData && !companiesData) return null;

    // Distribución de score por banda. El backend ya devuelve score_low/medium/high
    // calculadas sobre la escala real (0-10) — ver backend/utils/leadScore.js.
    // Antes se recomponían desde buckets 0-20/21-40/... de una escala 0-100 que no
    // existe, y los 204 leads reales caían todos en "bajo".
    const leadScoreData = leadsData?.score_distribution ? {
      low: parseInt(leadsData.score_distribution.score_low || 0),
      medium: parseInt(leadsData.score_distribution.score_medium || 0),
      high: parseInt(leadsData.score_distribution.score_high || 0)
    } : { low: 0, medium: 0, high: 0 };

    // Use consistent lead definition between KPI and chart
    const leadScoreTotal = leadScoreData.low + leadScoreData.medium + leadScoreData.high;

    // Use API data directly for most stats - FIXED: Use consistent lead definition
    const stats = {
      totalLeads: leadScoreTotal || parseInt(leadsData?.total_leads || 0),
      qualifiedLeads: parseInt(analyticsData?.current_period?.qualified_leads || leadsData?.mqls || 0),
      pipelineValue: parseFloat(analyticsData?.current_period?.pipeline_value || 0),
      conversionRate: parseFloat(analyticsData?.current_period?.conversion_rate || leadsData?.conversion_rate_30d || 0),
      totalContacts: parseInt(contactsData?.total_contacts || 0), // Analytics doesn't have contacts, use contacts API
      totalCompanies: parseInt(companiesData?.total_companies || 0), // Analytics doesn't have companies, use companies API
      todaysActivities: parseInt(analyticsData?.current_period?.total_activities || 0),
      thisMonthDeals: parseInt(analyticsData?.current_period?.deals_won || 0),
      // Changes from the analytics API (`changes.*` is the only place the backend
      // computes period-over-period deltas — see backend/models/Analytics.js).
      // Fields without a real backend counterpart are null so no fake trend renders.
      leadsChange: analyticsData?.changes?.leads_change != null
        ? parseFloat(analyticsData.changes.leads_change)
        : null,
      qualifiedChange: null,
      pipelineChange: null,
      conversionChange: null,
      contactsChange: null,
      companiesChange: null,
      dealsChange: null
    };

    // Pipeline distribution - represent different stages, not totals.
    // NOTE: the analytics API has no `pipeline_distribution`; compute from real fields.
    // leads API folds SQLs into `mqls`, so use contacts stats (which split mql/sql) here.
    const pipelineData = {
      prospecting: parseInt(leadsData?.subscribers || 0) + parseInt(leadsData?.leads || 0), // Early stage leads
      qualification: parseInt(contactsData?.mqls || 0), // Marketing qualified leads
      proposal: parseInt(contactsData?.sqls || 0) + parseInt(contactsData?.opportunities || 0), // Sales qualified leads + opportunities
      closed: parseInt(contactsData?.customers || 0) // Closed deals
    };

    // Recent activities - use real activities data from API
    const recentActivities = activitiesData?.map(activity => ({
      id: activity.id,
      type: activity.type || 'update',
      description: activity.subject || activity.description || 'Atividade sem descrição',
      time: activity.activity_date || activity.created_at,
      user: activity.user_name || activity.owner_name || 'Usuário desconhecido',
      status: activity.is_completed ? 'completed' : 'pending'
    })) || [];

    // Resumen comercial: sólo métricas que NO duplican la fila de KPIs.
    const summaryData = {
      leadsLast7Days: parseInt(leadsData?.created_last_7_days || 0),
      leadsLast30Days: parseInt(leadsData?.created_last_30_days || 0),
      highScoreLeads: leadScoreData.high,
      openOpportunities: parseInt(contactsData?.opportunities || 0),
      totalContacts: parseInt(contactsData?.total_contacts || 0),
      totalCompanies: parseInt(companiesData?.total_companies || 0),
      activities30Days: parseInt(analyticsData?.current_period?.total_activities || 0),
    };

    return {
      stats,
      leadScoreData,
      avgLeadScore: parseFloat(leadsData?.avg_lead_score || 0),
      pipelineData,
      summaryData,
      recentActivities
    };
  }, [leadsData, contactsData, companiesData, analyticsData, activitiesData]);

  if (showLoadingState) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="animate-pulse">
          {/* Header skeleton */}
          <div className="mb-8">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
          
          {/* Stats cards skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                    <div className="ml-4 flex-1">
                      <div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
                      <div className="h-6 bg-gray-200 rounded w-16"></div>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-gray-200 rounded mr-1"></div>
                    <div className="h-4 bg-gray-200 rounded w-10"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Charts skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
              <div className="h-64 bg-gray-200 rounded"></div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
              <div className="h-64 bg-gray-200 rounded"></div>
            </div>
          </div>
          
          {/* Secondary grid skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-1"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
              <div className="space-y-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="flex justify-between items-center">
                    <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                    <div className="h-4 bg-gray-200 rounded w-16"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Show partial data even if some queries fail
  const hasError = !contactsData && !companiesData && !leadsData;
  
  if (hasError && !showLoadingState) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Dados parcialmente indisponíveis
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>Alguns dados não puderam ser carregados. Verifique sua conexão e tente novamente.</p>
              </div>
              <div className="mt-4">
                <button 
                  onClick={() => window.location.reload()}
                  className="bg-yellow-100 hover:bg-yellow-200 text-yellow-800 px-3 py-1 rounded text-sm"
                >
                  Tentar novamente
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Welcome header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Bem-vindo, {user?.first_name}! 👋
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
          Aqui está um resumo da atividade no CRM WA Contact Center
        </p>
      </div>

      {/* Load error banner - surface query failures instead of a blank screen */}
      {queryErrors.length > 0 && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Não foi possível carregar alguns dados
              </h3>
              <ul className="mt-1 text-sm text-red-700 list-disc list-inside">
                {queryErrors.map((q) => (
                  <li key={q.label}>
                    {`Não foi possível carregar ${q.label}`}
                    {q.error?.response?.status ? ` (erro ${q.error.response.status})` : ''}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Dashboard stats cards */}
      {dashboardData?.stats ? (
        <DashboardStats data={dashboardData.stats} userRole={user?.role?.name || user?.role} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
            <p className="text-yellow-700 text-sm">
              {analyticsError ? 
                `Analytics Error: ${analyticsError.message}` : 
                'Carregando estatísticas...'
              }
            </p>
            {analyticsError && (
              <p className="text-red-600 text-xs mt-2">
                Status: {analyticsError.response?.status} - Check browser console for details
              </p>
            )}
          </div>
        </div>
      )}

      {/* Fila 2 — tres columnas de igual peso.
          Antes: donut a 2/3 + pipeline a 1/3, con la tarjeta del donut medio vacía. */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 mt-6 items-stretch">
        <LeadScoreChart
          data={dashboardData?.leadScoreData}
          avgScore={dashboardData?.avgLeadScore}
        />
        <PipelineOverview data={dashboardData?.pipelineData} />
        <div className="md:col-span-2 xl:col-span-1">
          <CommercialSummary data={dashboardData?.summaryData} />
        </div>
      </div>

      {/* Fila 3 — actividades (2/3) + atajos (1/3).
          Antes la segunda columna estaba gateada a dos roles que casi nadie tiene,
          así que "Atividades Recentes" quedaba sola con medio dashboard en blanco. */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 mt-5 items-start">
        <div className="xl:col-span-2">
          {dashboardData?.recentActivities ? (
            <RecentActivities
              data={dashboardData.recentActivities}
              userId={user?.id}
              userScope={user?.permissions?.scope}
            />
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-4">Atividades Recentes</h3>
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="animate-pulse flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-1"></div>
                      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <QuickActions />
      </div>
    </div>
  );
};

export default DashboardPage;